import { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { APIScopeData, QueryBuilderQuery } from '@ardoq/api-types';
import {
  ButtonWithDropdown,
  DropdownOption,
  useOnOptionsChangedSubscription,
} from '@ardoq/dropdown-menu';
import { FormSize, TextInput } from '@ardoq/forms';
import { FlexBox } from '@ardoq/layout';
import { Locale } from '@ardoq/locale';
import { connect, dispatchAction } from '@ardoq/rxbeach';
import {
  selectComponentSelectionChanged,
  setComponentSelectQuery,
} from '../../actions';
import {
  applyInitialTreeConfiguration,
  disabledOptionsChanged,
  permissionContextChanged,
  setFilterTerm,
} from '../../actions/navigatorActions';
import { SelectComponentPaneRoot } from '../SelectComponentPaneRoot';
import type { PermissionContext } from '@ardoq/access-control';
import {
  selectComponentsEnhancedScopeData$,
  selectComponentsWorkspaceOptions$,
} from '../selectComponentHierarchyPaneConfiguration';
import type { EnhancedScopeData } from '@ardoq/data-model';
import { actionNamespaceSelectComponentHierarchyPane } from '@ardoq/global-consts';
import { getOnKeyDown } from './onKeyDown';
import { isSameSet } from '@ardoq/common-helpers';
import { componentTreeSelectionChanged } from '@ardoq/component-selection';
import { isEqual } from 'lodash';
import { IconButton } from '@ardoq/button';
import { IconName } from '@ardoq/icons';
import { colors } from '@ardoq/design-tokens';
import { getWorkspaceOptionsTooltip } from '../../utils/utils';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale, t } from '@ardoq/i18n';

export type InitialTreeConfiguration = {
  filterTerm?: string;
  componentId?: string;
};

type ComponentSelectorPaneBaseProps = {
  componentQuery?: QueryBuilderQuery | null;
  onComponentsSelected: (scopeData: APIScopeData) => void;
  selectedIds: string[];
  disabledOptions: string[];
  locale: Locale;
  i18nLocale?: I18nLocale;
  onCreateNewComponent?: (name: string) => void;
  placeholderText?: string;
  onFilterTermChange?: (filterTerm: string) => void;
  searchHintMessage?: string;
  /**
   * Collapses the result list below the search input while keeping it
   * mounted.
   */
  isResultListHidden?: boolean;
  autoFocus?: boolean;
  isMultiselectable?: boolean;
  /**
   * While a search term is set, expands every result deeply instead of only
   * the workspace roots.
   */
  isAsyncSearch?: boolean;
  /**
   * Hides the workspace-filter dropdown next to the search input. Has no
   * effect when `onEditSearchQuery` is provided.
   */
  hideWorkspaceFilter?: boolean;
  /**
   * If provided, the workspace-filter dropdown next to the search input is
   * replaced with a single icon button that fires this callback. Use this to
   * surface a custom "edit search query" affordance.
   */
  onEditSearchQuery?: () => void;
  /**
   * Number of user-applied filters on top of the base `componentQuery`. When
   * > 0 and `onEditSearchQuery` is set, a small count badge overlays the edit
   * button. Counts ≥ 10 render as "9+".
   */
  appliedFilterCount?: number;
  /**
   * Optional configuration applied once after the data for `componentQuery`
   * loads. The `filterTerm` is set on the search input and the navigator tree
   * scrolls `componentId` into view (expanding its ancestors). It is only
   * re-applied when `componentQuery` changes; updates to this prop alone are
   * ignored.
   */
  initialTreeConfiguration?: InitialTreeConfiguration;
  /**
   * Whether components that do not match the search term themselves — they are
   * only listed to give a match its hierarchy context — count as options in
   * their own right. They are then selectable, judged by `disabledOptions` like every other option, and no longer dimmed for
   * failing to match. Defaults to `false`, which offers only the components
   * the search returned and dims the rest.
   */
  areNonMatchingResultsSelectable?: boolean;
  /** Called with the scope data the listed components are rendered from. */
  onScopeDataLoaded?: (scopeData: EnhancedScopeData) => void;
};

/**
 * A permission context is required alongside the preview, not optional: an
 * empty one leaves `confidentialFieldPermissions` null, which makes
 * `canSeeConfidentialFieldByName` return true and renders confidential fields
 * unredacted. Enabling previews also requires the host app to register
 * `componentPreviewsRoutines`.
 */
type HoverPreviewProps =
  | { hasHoverPreview?: false; permissionContext?: undefined }
  | { hasHoverPreview: true; permissionContext: PermissionContext };

export type ComponentSelectorPaneProps = ComponentSelectorPaneBaseProps &
  HoverPreviewProps;

const actionNamespace = actionNamespaceSelectComponentHierarchyPane;
const onKeyDown = getOnKeyDown(actionNamespaceSelectComponentHierarchyPane);

const usePropMemo = <T,>(value: T | null): T | null => {
  const ref = useRef<T | null>(null);
  if (!isEqual(ref.current, value)) {
    ref.current = value;
  }
  return ref.current;
};

export const ComponentSelectorPane = ({
  componentQuery,
  onComponentsSelected,
  selectedIds,
  disabledOptions,
  locale,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
  onCreateNewComponent,
  onEditSearchQuery,
  appliedFilterCount = 0,
  initialTreeConfiguration,
  placeholderText,
  onFilterTermChange,
  searchHintMessage,
  isResultListHidden = false,
  autoFocus = false,
  isMultiselectable = true,
  isAsyncSearch = false,
  hideWorkspaceFilter = false,
  hasHoverPreview = false,
  areNonMatchingResultsSelectable = false,
  permissionContext,
  onScopeDataLoaded,
}: ComponentSelectorPaneProps) => {
  const resolvedPlaceholderText =
    placeholderText ??
    t(
      i18nLocale,
      'componentHierarchies:componentSelectorPane.placeholder',
      'Select one or more components'
    );
  const currentSelectionIds = useRef<string[]>(selectedIds);
  const wrappedOnComponentsSelected = (scopeData: APIScopeData) => {
    currentSelectionIds.current = scopeData.scopeComponents;
    onComponentsSelected(scopeData);
  };

  const inputRef = useRef<HTMLInputElement>(null);

  // Always read the latest value from the query useEffect without depending on
  // it — the configuration is only consumed when `componentQuery` changes.
  const initialTreeConfigurationRef = useRef(initialTreeConfiguration);
  useEffect(() => {
    initialTreeConfigurationRef.current = initialTreeConfiguration;
  }, [initialTreeConfiguration]);

  useEffect(() => {
    if (isSameSet(selectedIds, currentSelectionIds.current)) return;
    dispatchAction(
      componentTreeSelectionChanged({ treeSelection: selectedIds }),
      actionNamespace
    );
  }, [selectedIds]);

  const stableQuery = usePropMemo<QueryBuilderQuery>(componentQuery ?? null);

  useEffect(() => {
    const initial = initialTreeConfigurationRef.current;
    dispatchAction(setComponentSelectQuery({ query: null }), actionNamespace);
    dispatchAction(setFilterTerm(''), actionNamespace);
    // Pre-populate the (uncontrolled) input so the user sees the initial filter
    // term immediately. The corresponding `setFilterTerm` dispatch happens once
    // the data has loaded — see `getApplyInitialTreeConfigurationRoutine`.
    if (inputRef.current) {
      inputRef.current.value = initial?.filterTerm ?? '';
    }
    if (stableQuery) {
      dispatchAction(
        setComponentSelectQuery({ query: stableQuery }),
        actionNamespace
      );
      if (initial && (initial.filterTerm || initial.componentId)) {
        dispatchAction(
          applyInitialTreeConfiguration({
            filterTerm: initial.filterTerm,
            componentId: initial.componentId,
          }),
          actionNamespace
        );
      }
    }
  }, [stableQuery]);

  useEffect(() => {
    dispatchAction(disabledOptionsChanged(disabledOptions), actionNamespace);
  }, [disabledOptions]);

  useEffect(() => {
    if (!permissionContext) return;
    dispatchAction(
      permissionContextChanged(permissionContext),
      actionNamespace
    );
  }, [permissionContext]);

  useEffect(() => {
    if (!onScopeDataLoaded) return;
    const subscription =
      selectComponentsEnhancedScopeData$.subscribe(onScopeDataLoaded);
    return () => subscription.unsubscribe();
  }, [onScopeDataLoaded]);

  useOnOptionsChangedSubscription(
    wrappedOnComponentsSelected,
    selectComponentSelectionChanged,
    actionNamespace
  );

  return (
    // The padding is to  make sure the focus highlight of the search input is
    // not cut off.
    <FlexBox
      flexDirection="column"
      gap="small"
      height="full"
      padding="medium"
      width="full"
    >
      <FlexBox flexDirection="row" gap="small">
        <TextInput
          onValueChange={filterTerm => {
            dispatchAction(setFilterTerm(filterTerm), actionNamespace);
            onFilterTermChange?.(filterTerm);
          }}
          onKeyDown={onKeyDown}
          placeholder={resolvedPlaceholderText}
          hintMessage={searchHintMessage}
          ref={inputRef}
          autoFocus={autoFocus}
          formSize={FormSize.FULL}
        />
        {onEditSearchQuery ? (
          <EditSearchQueryButton
            onClick={onEditSearchQuery}
            i18nLocale={i18nLocale}
            appliedFilterCount={appliedFilterCount}
          />
        ) : (
          !hideWorkspaceFilter && (
            <WorkspaceOptionsConnected i18nLocale={i18nLocale} />
          )
        )}
      </FlexBox>
      <SelectComponentPaneRoot
        i18nLocale={i18nLocale}
        locale={locale}
        onCreateNewComponent={onCreateNewComponent}
        hasHoverPreview={hasHoverPreview}
        isMultiselectable={isMultiselectable}
        isAsyncSearch={isAsyncSearch}
        areNonMatchingResultsSelectable={areNonMatchingResultsSelectable}
        isHidden={isResultListHidden}
      />
    </FlexBox>
  );
};

type WorkspaceOptionsProps = {
  options: DropdownOption[];
  i18nLocale: I18nLocale;
};

const WorkspaceOptions = ({ options, i18nLocale }: WorkspaceOptionsProps) => (
  <ButtonWithDropdown
    iconName={IconName.FILTER_LIST}
    tooltipText={getWorkspaceOptionsTooltip(options, i18nLocale)}
    options={options}
    isKeepOpen={true}
    isDisabled={options.length <= 1}
  />
);

const WorkspaceOptionsConnected = connect(
  WorkspaceOptions,
  selectComponentsWorkspaceOptions$
);

const MAX_BADGE_COUNT = 9;

const formatBadgeCount = (count: number): string =>
  count > MAX_BADGE_COUNT ? `${MAX_BADGE_COUNT}+` : `${count}`;

const ButtonAndBadge = styled.div`
  position: relative;
  display: inline-flex;
`;

const CountBadge = styled.span`
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 999px;
  background-color: ${colors.surfaceBrandStrong};
  color: ${colors.textOnInverted};
  font-size: 10px;
  font-weight: 600;
  line-height: 16px;
  text-align: center;
  box-sizing: border-box;
  pointer-events: none;
`;

type EditSearchQueryButtonProps = {
  onClick: () => void;
  i18nLocale: I18nLocale;
  appliedFilterCount: number;
};

const getEditSearchQueryLabel = (
  i18nLocale: I18nLocale,
  appliedFilterCount: number
): string => {
  if (appliedFilterCount <= 0) {
    return t(
      i18nLocale,
      'componentHierarchies:componentSelectorPane.editSearchQuery',
      'Edit search query'
    );
  }
  return t(
    i18nLocale,
    appliedFilterCount === 1
      ? 'componentHierarchies:componentSelectorPane.editSearchQueryWithCount'
      : 'componentHierarchies:componentSelectorPane.editSearchQueryWithCount_plural',
    { count: appliedFilterCount },
    `Edit search query (${appliedFilterCount} filter${appliedFilterCount === 1 ? '' : 's'} applied)`
  );
};

const EditSearchQueryButton = ({
  onClick,
  i18nLocale,
  appliedFilterCount,
}: EditSearchQueryButtonProps) => (
  <ButtonAndBadge>
    <IconButton
      iconName={IconName.FILTER_LIST}
      label={getEditSearchQueryLabel(i18nLocale, appliedFilterCount)}
      onClick={onClick}
    />
    {appliedFilterCount > 0 && (
      <CountBadge aria-hidden="true">
        {formatBadgeCount(appliedFilterCount)}
      </CountBadge>
    )}
  </ButtonAndBadge>
);
